import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {siteSiteAssocsSelector, useGetAllSiteSiteAssocsQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {SiteSiteAssocDataTable} from "../../components/data-table/SiteSiteAssocDataTable/SiteSiteAssocDataTable";
import {ExportLink} from "../../components/exportable/ExportLink/ExportLink";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

export type SiteMileageDataTablePageProps = {}

export const SiteMileageDataTablePage = (props: SiteMileageDataTablePageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectSiteSiteAssocs = useMemo(() => {
    return siteSiteAssocsSelector();
  }, []);

  const {siteSiteAssocs, isLoading, error, refetch,} = useGetAllSiteSiteAssocsQuery(access ? (regionId ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      siteSiteAssocs: selectSiteSiteAssocs(result, {
        effectiveOnly: true,
      }),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  return (
    <ProtectedPage permissions={["site_view"]} isLoading={isLoading} error={error} onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("common.page.sitemileage.title", "Site Mileages", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <ExportLink data={siteSiteAssocs}>
                          <Menu.Item>
                            {t("common.action.export", "Export", {ns: "common"})}
                          </Menu.Item>
                        </ExportLink>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/pipelines/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <SiteSiteAssocDataTable
                  withBorder
                  records={siteSiteAssocs}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}
                  showSource={true}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
